import { useLingui } from '@lingui/react/macro';
import { ErrorBoundary as SentryErrorBoundary } from '@sentry/react';
import { Back, Refresh } from 'iconsax-reactjs';
import type { FC, ReactNode } from 'react';
import { BASE_URL } from '@/base/constants';
import { Button } from '@/shared/Button';
import { cn } from '@/shared/react';

export const ErrorBoundary: FC<{ children?: ReactNode }> = ({ children }) => {
  const { t } = useLingui();

  const parseError = (e: unknown): { message: string; status: number } => {
    const defaultMessage = t`A temporary issue occurred. Our technical team is working to fix it as soon as possible.`;
    if (e instanceof Error) {
      const rawMessage = e.message;
      const [status, ...rest] = rawMessage.split(':');
      if (['404'].includes(status)) {
        return {
          status: +status,
          message: rest.join(':').trim() || defaultMessage,
        };
      }
      return {
        status: 500,
        message: rest.join(':').trim() || defaultMessage,
      };
    }
    return {
      status: 500,
      message: defaultMessage,
    };
  };

  return (
    <SentryErrorBoundary
      fallback={(x) => {
        const { message, status } = parseError(x.error);
        const bgColorClassName =
          status === 500
            ? 'bg-background-negative-subtle'
            : 'bg-background-primary';
        const textColorClassName =
          status === 500 ? 'text-shades-white-100' : 'text-content-primary';
        return (
          <main
            className={cn(
              'p-3 h-svh w-full flex flex-col items-center justify-center text-center',
              bgColorClassName,
            )}
          >
            <h2 className={cn('text-8xl mb-1', textColorClassName)}>
              {status === 404 ? 404 : t`Sorry!`}
            </h2>
            <p className={cn('text-lg mb-8 max-w-md', textColorClassName)}>
              {message}
            </p>
            <div className="flex gap-3 items-center">
              {status === 404 && (
                <Button
                  type="button"
                  onClick={() => {
                    window.location.href = BASE_URL;
                    return new Promise(() => {});
                  }}
                  variant="outline"
                  className="min-w-48"
                  size={12}
                >
                  <Back size={16} className="me-1" />
                  {t`Back to Home`}
                </Button>
              )}
              {status !== 404 && (
                <Button
                  type="button"
                  onClick={() => {
                    window.location.reload();
                    return new Promise(() => {});
                  }}
                  variant="outline"
                  className="min-w-48"
                  size={12}
                >
                  <Refresh size={16} className="me-1" />
                  {t`Reload the Page`}
                </Button>
              )}
            </div>
          </main>
        );
      }}
    >
      {children}
    </SentryErrorBoundary>
  );
};
